import mixpanel from 'mixpanel-browser';
import { EVENTS } from './events';
import { sanitizeProperties } from './blocklist';
import analyticsStorage from './storage';
import syncManager from './sync';
import { getActiveTextModeContext, resolveAssessmentMode } from '@/features/text-mode/analytics/text-mode-context';
import pinia from '@/stores';
import { useLanguageStore } from '@/stores/language';
import { useRegistrationStore } from '@/stores/registration';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { usePreferenceStore } from '@/stores/preference';
import { buildEncounterDetails } from '@/utils/encounterDetails';

const MIXPANEL_TOKEN = (window?.thinkmd && window?.thinkmd?.mixpanel_token) || process.env.VUE_APP_MIXPANEL_TOKEN;
const IS_DEV = process.env.NODE_ENV !== 'production';

function resolveAnalyticsFlag() {
  if (IS_DEV) {
    try {
      const localOverride = localStorage.getItem('analytics_enabled');
      if (localOverride === 'true') {
        return true;
      }
      if (localOverride === 'false') {
        return false;
      }
    } catch (error) {
      console.warn('[Analytics] Unable to read analytics_enabled from localStorage:', error);
    }
  }

  if (window?.thinkmd && typeof window?.thinkmd?.analytics_enabled === 'boolean') {
    return window?.thinkmd?.analytics_enabled;
  }

  return false;
}

// resolveAnalyticsFlag is called at runtime each time — not stored as a build-time const
function isAnalyticsEnabled() {
  return resolveAnalyticsFlag();
}

const CONTEXT_STEP_PROPERTIES = {
  stepIndex: 'step_index',
  stepCount: 'step_count',
  assessmentPath: 'assessment_path',
};

let mixpanelInitialized = false;
let currentUserId = null;
let currentVisitId = null;
const STORAGE_KEY_USER_ID = 'analytics_user_id';

function ensureMixpanelInitialized() {
  if (mixpanelInitialized) return;
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: IS_DEV,
    track_pageview: true,
    persistence: 'localStorage',
    ignore_dnt: false,
  });

  syncManager.init(MIXPANEL_TOKEN);
  mixpanelInitialized = true;
}

/**
 * Resolves the user ID in priority order:
 * preference store → session details
 * @returns {string|number|null}
 */
function resolveUserId() {
  try {
    const userId = usePreferenceStore(pinia).getResponse('user_id', 'number')?.value;
    if (userId) return userId;

    const sessionDetails = buildEncounterDetails();
    return sessionDetails?.user?.id || null;
  } catch (e) {
    return null;
  }
}

/**
 * Resolves the panel_name from the route params, then falls back to the URL hash.
 * @returns {string|null}
 */
function resolvePanelName() {
  // required lazily: the router requires this module back from inside its own guards
  const routePanelName = require('@/router').default?.currentRoute?.params?.panels_name;
  if (routePanelName) return routePanelName;

  // Fallback: extract from hash route (e.g. /#/past-visits → past-visits)
  const hashPath = window.location.hash?.replace(/^#\/?/, '').split('?')[0];
  return hashPath || null;
}

function buildEventProperties(event, data = {}) {
  const payload = {
    ...data,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    page_path: window.location.pathname,
  };

  if (event && !event.startsWith('$')) {
    const partnerConfigStore = usePartnerConfigStore();
    if (partnerConfigStore.code) {
      payload.encounter_code = partnerConfigStore.code;
    }
  }

  const languageStore = useLanguageStore(pinia);
  payload.language = languageStore.selected;

  const registrationStore = useRegistrationStore(pinia);
  if (registrationStore.hrId) {
    payload.hr_id = registrationStore.hrId;
  }

  const activeContext = getActiveTextModeContext();
  if (activeContext) {
    if (payload.mode === undefined) {
      payload.mode = resolveAssessmentMode(activeContext);
    }
    if (!payload.panel_name && activeContext.panelName) {
      payload.panel_name = activeContext.panelName;
    }
    Object.entries(CONTEXT_STEP_PROPERTIES).forEach(([contextKey, property]) => {
      if (activeContext[contextKey] !== undefined && payload[property] === undefined) {
        payload[property] = activeContext[contextKey];
      }
    });
  }

  const env = window?.thinkmd?.env;
  if (env && payload.env === undefined) {
    payload.env = env;
  }

  if (!payload.panel_name) {
    const panelName = resolvePanelName();
    if (panelName) payload.panel_name = panelName;
  }

  if (!payload.user_id) {
    const userId = resolveUserId();
    if (userId) payload.user_id = userId;
  }

  return sanitizeProperties(payload);
}

function resolveDistinctId(payload = {}) {
  return payload.user_id || currentUserId || getDeviceId();
}

async function queueOfflineEvent(event, payload) {
  try {
    await analyticsStorage.addEvent({
      event,
      properties: payload,
      userId: resolveDistinctId(payload),
      visitId: currentVisitId,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    if (IS_DEV) {
      console.error('[Analytics] Error queueing offline event:', error);
    }
  }
}

/**
 * Track custom events to Mixpanel
 * @param {string} event - Event name
 * @param {object} data - Event properties
 */
function track(event, data = {}) {
  ensureMixpanelInitialized();
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  try {
    const payload = buildEventProperties(event, data);

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      queueOfflineEvent(event, payload);
      return;
    }

    mixpanel.track(event, payload);
  } catch (error) {
    if (IS_DEV) {
      console.error('[Analytics] Error tracking event:', error);
    }
  }
}

/**
 * Get element metadata for click tracking
 * @param {HTMLElement} element - The clicked element
 * @returns {object} Element metadata
 */
function getElementMetadata(element) {
  const metadata = {
    tag_name: element.tagName.toLowerCase(),
    element_id: element.id || null,
    element_classes: element.className || null,
  };

  if (element.tagName === 'BUTTON') {
    metadata.button_type = element.type || 'button';
  }

  if (element.__vue__) {
    metadata.vue_component = element.__vue__.$options.name || element.__vue__.$options._componentTag || null;
  }

  return metadata;
}

let clickTrackingInitialized = false;
const recentEvents = new Set();

function setupGlobalClickTracking() {
  if (clickTrackingInitialized) return;
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  clickTrackingInitialized = true;

  document.addEventListener('click', (event) => {
    const element = event.target;
    const clickableElement = element.closest('button, a, [role="button"], [onclick]');

    if (clickableElement) {
      if (clickableElement.hasAttribute('data-analytics-skip')) {
        return;
      }

      const elementId = clickableElement.id || '';
      const elementClass = clickableElement.className || '';
      const elementText = (clickableElement.textContent || '').trim().substring(0, 20);
      const dedupKey = `${clickableElement.tagName}_${elementId}_${elementClass}_${elementText}`;
      
      if (recentEvents.has(dedupKey)) {
        return;
      }
      
      recentEvents.add(dedupKey);
      setTimeout(() => recentEvents.delete(dedupKey), 500);

      const metadata = getElementMetadata(clickableElement);
      track(EVENTS.CLICK_EVENT, metadata);
    }
  });
}

function identifyUser(userId, properties = {}) {
  ensureMixpanelInitialized();
  if (!MIXPANEL_TOKEN || !isAnalyticsEnabled()) return;

  try {
    mixpanel.identify(userId.toString());
    const profile = sanitizeProperties({
      user_type: properties.user_type || null,
      registration_id: properties.registration_id || null,
      hr_id: properties.hr_id || null,
      device_id: properties.device_id || null,
      last_login: new Date().toISOString(),
      ...properties
    });
    delete profile.name;
    mixpanel.people.set(profile);
  } catch (error) {
    if (IS_DEV) {
      console.error('[Analytics] Error identifying user:', error);
    }
  }
}

/**
 * Get or generate device ID
 * @returns {string} Device ID
 */
function getDeviceId() {
  const STORAGE_KEY = 'mixpanel_device_id';
  let deviceId = localStorage.getItem(STORAGE_KEY);

  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem(STORAGE_KEY, deviceId);
  }

  return deviceId;
}

function setUserId(userId) {
  currentUserId = userId;
  localStorage.setItem(STORAGE_KEY_USER_ID, userId);
}

function setVisitId(visitId) {
  currentVisitId = visitId;
}

async function getSyncStats() {
  return syncManager.getStats();
}

function triggerSync() {
  syncManager.syncNow();
}

const AnalyticsService = {
  install(Vue) {
    Vue.prototype.$track = track;
    Vue.prototype.$identifyUser = identifyUser;
    Vue.prototype.$setUserId = setUserId;
    Vue.prototype.$setVisitId = setVisitId;
    Vue.prototype.$getSyncStats = getSyncStats;
    Vue.prototype.$triggerSync = triggerSync;
    Vue.prototype.$getDeviceId = getDeviceId;
    Vue.prototype.$analyticsEvents = EVENTS;
    Vue.prototype.$isAnalyticsEnabled = isAnalyticsEnabled;
    Vue.mixin({
      mounted() {
        if (this === this.$root) {
          ensureMixpanelInitialized();
          if (IS_DEV) {
            if (!MIXPANEL_TOKEN) {
              console.warn('[Analytics] Mixpanel token not found. Analytics tracking is disabled.');
            }
            if (!isAnalyticsEnabled()) {
              console.info('[Analytics] Analytics tracking is DISABLED by feature flag.');
            }
          }
          setupGlobalClickTracking();
        }
      }
    });
  }
};

export { track, isAnalyticsEnabled, buildEventProperties };
export default AnalyticsService;
